import { ChevronUp, ChevronDown, Trash2, Plus, Trophy, MapPin, Flag, PartyPopper } from "lucide-react";
import { TopFiveDialog } from "./TopFiveDialog";

export const KIND = {
  country: { label: "Country", icon: Flag, color: "#34D399" },
  city: { label: "City", icon: MapPin, color: "#39FF88" },
  event: { label: "Moment", icon: PartyPopper, color: "#F59E0B" },
};

const MAX = 10;

const Slot = ({ item, i, total, b, items }) => {
  const K = KIND[item.kind];
  const extra = i >= 5;
  const move = (to) => { const next = [...items]; [next[i], next[to]] = [next[to], next[i]]; b.setTop5(next); };
  return (
    <li className={`group relative rounded-2xl border overflow-hidden hover:-translate-y-1 transition-[transform,border-color] duration-200 hover:border-emerald-400/30 animate-rise ${extra ? "border-white/[0.05] bg-[#101622] p-4 pt-3" : "border-white/[0.07] bg-[#131A26] p-5 pt-4"}`} style={{ animationDelay: `${i * 60}ms` }} data-testid={`top5-item-${i + 1}`}>
      <span className={`absolute -right-2 font-display font-bold leading-none text-white/[0.04] select-none ${extra ? "-bottom-5 text-[80px]" : "-bottom-8 text-[120px]"}`}>{i + 1}</span>
      <div className="flex items-center justify-between">
        <span className={`font-mono text-sm ${extra ? "text-slate-500" : "text-emerald-400"}`}>{String(i + 1).padStart(2, "0")}</span>
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: K.color }}><K.icon size={12} /> {K.label}</span>
      </div>
      <h4 className={`font-display font-semibold mt-3 leading-tight ${extra ? "text-base" : "text-xl"}`} data-testid={`top5-title-${i + 1}`}>{item.title}</h4>
      {item.subtitle && <p className="text-xs text-slate-400 mt-1">{item.subtitle}</p>}
      {item.note && <p className="text-sm text-slate-300/90 mt-3 italic">"{item.note}"</p>}
      {!b.readOnly && (
        <div className="relative flex gap-1 mt-4 opacity-60 group-hover:opacity-100 transition-opacity">
          <button disabled={i === 0} onClick={() => move(i - 1)} className="p-1 rounded text-slate-400 hover:text-white disabled:opacity-20" aria-label="Move up" data-testid={`top5-up-${i + 1}`}><ChevronUp size={15} /></button>
          <button disabled={i === total - 1} onClick={() => move(i + 1)} className="p-1 rounded text-slate-400 hover:text-white disabled:opacity-20" aria-label="Move down" data-testid={`top5-down-${i + 1}`}><ChevronDown size={15} /></button>
          <button onClick={() => b.setTop5(items.filter((_, j) => j !== i), "Removed from your top 5")} className="p-1 rounded text-slate-400 hover:text-red-400 ml-auto" aria-label="Remove" data-testid={`top5-delete-${i + 1}`}><Trash2 size={14} /></button>
        </div>
      )}
    </li>
  );
};

const EmptySlots = ({ from, to, text, small }) => Array.from({ length: to - from }).map((_, k) => (
  <li key={`empty-${from + k}`} className={`rounded-2xl border border-dashed border-white/10 flex flex-col justify-between text-slate-600 ${small ? "p-4 min-h-[110px]" : "p-5 min-h-[150px]"}`} data-testid={`top5-empty-${from + k + 1}`}>
    <span className="font-mono text-sm">{String(from + k + 1).padStart(2, "0")}</span>
    <span className="text-xs">{text}</span>
  </li>
));

export const TopFive = ({ data, b, names = [] }) => {
  const items = data.top5 || [];
  if (b.readOnly && items.length === 0) return null;
  return (
    <section className="px-4 sm:px-8 lg:px-12 pt-10 pb-4" data-testid="top5-section">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow flex items-center gap-2"><Trophy size={12} /> Hall of fame</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">My top 5, ever</h2>
        </div>
        {!b.readOnly && items.length < MAX && (
          <TopFiveDialog data={data} b={b} names={names} extra={items.length >= 5} onAdd={(it) => b.setTop5([...items, it], items.length < 5 ? `${it.title} is in your top 5` : `${it.title} added to your extras`)}>
            <button className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-emerald-500 text-[#04120c] hover:bg-emerald-400 transition-colors" data-testid="top5-add-button"><Plus size={14} /> {items.length < 5 ? "Add" : "Add extra"}</button>
          </TopFiveDialog>
        )}
      </div>
      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {items.slice(0, 5).map((it, i) => <Slot key={`${it.kind}-${it.title}-${i}`} item={it} i={i} total={items.length} b={b} items={items} />)}
        {!b.readOnly && items.length < 5 && <EmptySlots from={items.length} to={5} text="A country, city or moment you'll never forget" />}
      </ol>
      {(items.length > 5 || (!b.readOnly && items.length === 5)) && (
        <div className="mt-8" data-testid="top5-extras">
          <p className="eyebrow !text-slate-500 mb-3">Extras · {Math.max(items.length - 5, 0)}/5</p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {items.slice(5).map((it, k) => <Slot key={`${it.kind}-${it.title}-${k + 5}`} item={it} i={k + 5} total={items.length} b={b} items={items} />)}
            {!b.readOnly && <EmptySlots from={items.length} to={MAX} text="One more you can't leave out" small />}
          </ol>
        </div>
      )}
    </section>
  );
};
